/**
 * MessageBubble (KAN-621)
 *
 * Shared bubble of a client dialog message. Used by the contact transcript
 * dialog and the estimate brief chat, so both feeds render the same caption:
 * channel, time, and for OUR messages (role assistant) the Telegram account
 * they were sent from plus the delivery status (check marks).
 *
 * KAN-688: image attachments (photo of a manual message) show as a thumbnail
 * up to 240px; a click opens the full size in a new tab.
 *
 * KAN-772: an audio attachment (client voice message, round video, audio file)
 * shows a player above the message text, which carries the transcript.
 *
 * KAN-809: any other attachment with a url shows as a file row (MessageFiles);
 * a message without text and without shown attachments carries a muted
 * caption instead of an empty bubble: "Вложение" when it has attachments
 * (none of them with a url), "Сообщение без текста" otherwise.
 *
 * KAN-912: the root carries data-message-id; with onDelete OUR message the
 * server allows to delete (can_delete) gets the actions menu "Удалить" next
 * to the bubble. A client message never has it.
 */
import type { ReactNode } from 'react'
import { Check, CheckCheck } from 'lucide-react'
import type {
  EstimateMessage,
  MessageAttachment,
  MessageDelivery,
  MessageSenderAccount,
} from '@/types/estimate'
import { cn } from '@/lib/utils'
import { useApiFileSrc } from '@/hooks/useApiFileSrc'
import { senderAccountAlias } from '@/lib/senderAccount'
import { SenderAccountLink } from './SenderAccountLink'
import { MessageFiles } from './MessageFiles'
import { MessageActionsMenu } from './MessageActionsMenu'
import { isMessageDeletable } from '@/lib/messageDelete'

const APPROXIMATE_TITLE = 'Аккаунт определён по последнему диалогу клиента'

function formatReadAt(readAt: string | null | undefined): string {
  if (!readAt) return ''
  const date = new Date(readAt)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

interface MessageSenderProps {
  account: MessageSenderAccount
}

/**
 * Our Telegram account the message was sent from (KAN-645): avatar and short
 * alias, full name in the hint, the chip links to Settings > Telegram.
 */
export function MessageSender({ account }: MessageSenderProps) {
  if (!senderAccountAlias(account)) return null

  return (
    <span
      data-testid="message-sender"
      className="inline-flex min-w-0 items-center gap-1"
    >
      {account.approximate && (
        <span
          title={APPROXIMATE_TITLE}
          aria-label={APPROXIMATE_TITLE}
          className="cursor-help"
        >
          ~
        </span>
      )}
      <SenderAccountLink
        account={account}
        hintPrefix={account.approximate ? APPROXIMATE_TITLE : undefined}
        testId="message-sender-link"
        className="text-gray-600"
      />
    </span>
  )
}

interface MessageDeliveryMarkProps {
  delivery: MessageDelivery
  readAt?: string | null
}

/** Check marks: one = sent, two (accent) = read. */
export function MessageDeliveryMark({
  delivery,
  readAt,
}: MessageDeliveryMarkProps) {
  if (delivery === 'read') {
    const readTime = formatReadAt(readAt)
    const label = readTime ? `Прочитано ${readTime}` : 'Прочитано'
    return (
      <span title={label} className="inline-flex shrink-0">
        <CheckCheck
          role="img"
          aria-label={label}
          className="h-3.5 w-3.5 text-blue-500"
        />
      </span>
    )
  }
  if (delivery === 'sent') {
    return (
      <span title="Отправлено" className="inline-flex shrink-0">
        <Check role="img" aria-label="Отправлено" className="h-3.5 w-3.5" />
      </span>
    )
  }
  return null
}

interface MessageImagesProps {
  attachments: MessageAttachment[] | undefined
}

/** Image attachments: thumbnails linking to the full size (new tab). */
export function MessageImages({ attachments }: MessageImagesProps) {
  const images = (attachments ?? []).filter(
    (item) => item.type === 'image' && item.url
  )
  if (images.length === 0) return null
  return (
    <div className="mb-1 flex flex-wrap gap-1" data-testid="message-images">
      {images.map((image) => (
        <MessageImage key={image.url} image={image} />
      ))}
    </div>
  )
}

/**
 * One thumbnail. KAN-770: the file is served by the authenticated API
 * (/api/...), so it is loaded with the token into an object URL; an S3 link
 * is used as is. Nothing is shown until the picture is loaded.
 */
function MessageImage({ image }: { image: MessageAttachment }) {
  const src = useApiFileSrc(image.url)
  if (!src) return null
  const title = image.name || 'Фото'
  return (
    <a
      href={src}
      target="_blank"
      rel="noopener noreferrer"
      title={title}
      data-testid="message-image-link"
    >
      <img
        src={src}
        alt={title}
        loading="lazy"
        className="block max-h-[240px] max-w-[240px] rounded-md object-contain"
      />
    </a>
  )
}

interface MessageAudiosProps {
  attachments: MessageAttachment[] | undefined
}

/** Audio attachments: a player per file (KAN-772). */
export function MessageAudios({ attachments }: MessageAudiosProps) {
  const audios = (attachments ?? []).filter(
    (item) => item.type === 'audio' && item.url
  )
  if (audios.length === 0) return null
  return (
    <div className="mb-1 flex flex-col gap-1">
      {audios.map((audio) => (
        <MessageAudio key={audio.url} audio={audio} />
      ))}
    </div>
  )
}

/**
 * One player. The file is loaded like a picture (KAN-770): an /api/ address
 * with the token into an object URL, an S3 link as is.
 */
function MessageAudio({ audio }: { audio: MessageAttachment }) {
  const src = useApiFileSrc(audio.url)
  if (!src) return null
  return (
    <audio
      controls
      preload="metadata"
      src={src}
      title={audio.name || 'Голосовое сообщение'}
      data-testid="message-audio"
      className="block h-10 w-64 max-w-full"
    />
  )
}

interface MessageBubbleProps {
  message: EstimateMessage
  /** Channel marker (badge or short text): each feed keeps its own look. */
  channel: ReactNode
  /** Already formatted message time. */
  time: string
  /** Extra classes for the caption row (gap between caption items). */
  captionClassName?: string
  testId?: string
  /** KAN-912: delete handler; the menu is shown only for a deletable message. */
  onDelete?: (message: EstimateMessage) => void
  /** KAN-912: finger-sized menu button (app /m). */
  touchActions?: boolean
}

export function MessageBubble({
  message,
  channel,
  time,
  captionClassName = 'gap-2',
  testId,
  onDelete,
  touchActions = false,
}: MessageBubbleProps) {
  const isClient = message.role === 'user'
  // Sender and delivery marks describe OUR outgoing messages only.
  const sender = !isClient ? message.sender_account : null
  const delivery = !isClient ? message.delivery : null
  // Every attachment with a url is drawn: picture, player or file row.
  const attachments = message.attachments ?? []
  const hasShownAttachment = attachments.some((item) => Boolean(item.url))
  const emptyCaption = attachments.length > 0 ? 'Вложение' : 'Сообщение без текста'
  const showActions = !!onDelete && isMessageDeletable(message)

  return (
    <div
      data-testid={testId}
      data-message-id={message.id}
      className={cn('flex items-start gap-1', isClient ? 'justify-end' : 'justify-start')}
    >
      <div
        className={cn(
          'max-w-[80%] rounded-lg px-3 py-2 text-sm',
          isClient ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-900'
        )}
      >
        <MessageImages attachments={message.attachments} />
        <MessageAudios attachments={message.attachments} />
        <MessageFiles attachments={message.attachments} onDark={isClient} />
        {message.content && (
          <div className="whitespace-pre-wrap break-words">{message.content}</div>
        )}
        {!message.content && !hasShownAttachment && (
          <div className={cn('italic', isClient ? 'text-blue-100' : 'text-gray-400')}>
            {emptyCaption}
          </div>
        )}
        <div
          className={cn(
            'mt-1 flex flex-wrap items-center text-xs',
            captionClassName,
            isClient ? 'text-blue-100' : 'text-gray-400'
          )}
        >
          {channel}
          {sender && <MessageSender account={sender} />}
          <span>{time}</span>
          {delivery && (
            <MessageDeliveryMark delivery={delivery} readAt={message.read_at} />
          )}
        </div>
      </div>
      {showActions && onDelete && (
        <MessageActionsMenu onDelete={() => onDelete(message)} touch={touchActions} />
      )}
    </div>
  )
}
